import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime, nextRelativeTickDelay, formatDateTime } from '@/shared/utils/formatDate';

/**
 * 後端時間字串 → 會自己更新的相對時間文字。
 *
 * 為什麼需要 tick：相對時間是「算出來的」，貼文卡片停在畫面上不動時，
 * 「1 分鐘前」會一直停在那裡，過了十分鐘還是寫 1 分鐘前。
 *
 * 刷新節奏由 nextRelativeTickDelay 決定（一小時內每分鐘、之後對齊整點、超過七天停止），
 * 所以「3 小時前」放著不動也會自己變成「4 小時前」，不會停在剛掛載時的值。
 *
 * 用 setTimeout 而不是 setInterval，並把 tick 放進依賴：每次醒來都重新問一次下一段間隔，
 * 於是同一張卡片會自然從「每分鐘」降頻成「每小時」，跨過七天後停掉。
 * 若用 setInterval + 只依賴 str，effect 不會因為 tick 重跑，頻率永遠降不下來。
 *
 * 回傳 title 給呼叫端掛在 <time title>：相對時間再方便，使用者想確認精確時間時
 * 還是要看得到，hover 給絕對時間是社群平台的通例。
 *
 * @param {string} str 後端時間字串（YYYY-MM-DD HH:mm:ss，UTC）
 * @returns {{ text: string, title: string }}
 */
export function useRelativeTime(str) {
  // i18n 語言切換時要重算：formatRelativeTime 讀的是 i18n.language 這個模組層單例，
  // 不透過 useTranslation 訂閱的話，切語言後已掛載的卡片會停在舊語言
  useTranslation();

  const [tick, setTick] = useState(0);

  useEffect(() => {
    const delay = nextRelativeTickDelay(str);
    if (delay === null) return;
    const id = setTimeout(() => setTick((n) => n + 1), delay);
    return () => clearTimeout(id);
  }, [str, tick]);

  return {
    text: formatRelativeTime(str),
    title: formatDateTime(str),
  };
}
